'use client';

import { ArrowLeft01Icon } from '@terra/ui-icons';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../../components/button';
import { IconButton } from '../../../components/icon-button';
import {
  BodyDefault,
  BodyStrong,
  LabelDefault,
  MetaDefault,
} from '../../../components/typography';
import type { ChatObjectNavigationTarget } from '../components/hitl-types';
import { getToolConfig } from '../components/tool-config';
import { prepareToolResultData } from '../components/ToolCallsRenderers/content-detection';
import { ToolResultRenderer } from '../components/ToolCallsRenderers/ToolResultRenderer';
import { AgentStatusDots } from './AgentStatusDots';
import {
  isParallelAgentAlive,
  parallelAgentStatus,
} from './parallel-agent-batch';
import type { ParallelAgentExecution } from './parallel-agent-execution';
import {
  collectSubagentActivity,
  type SubagentOpenTarget,
} from './subagent-activity';
import { readSubagentTask } from './subagent-task';
import { SubagentActivityLine } from './SubagentActivityLine';
import { SubagentParentMessage } from './SubagentParentMessage';

export function SubagentActivityPanel({
  execution,
  messages,
  onOpenActivity,
  onOpenObject,
  renderNeoSteps,
  onBack,
}: {
  execution: ParallelAgentExecution;
  messages: TerraUIMessage[];
  onOpenActivity?: (target: SubagentOpenTarget) => void;
  onOpenObject?: (target: ChatObjectNavigationTarget) => void;
  renderNeoSteps?: (threadId: string) => ReactNode;
  onBack: () => void;
}) {
  const t = useTranslations('designSystem.chat.parallelAgents');
  const tStatus = useTranslations(
    'designSystem.chat.parallelAgents.agentStatus',
  );
  const isNeo = execution.kind === 'neo';
  const neoSteps =
    isNeo && renderNeoSteps ? renderNeoSteps(execution.agentThreadId) : null;
  const showsNeoSteps = neoSteps !== null;
  const config = getToolConfig(execution.agentType);
  const name = isNeo ? t('neoAgentName') : config.name;
  const status = parallelAgentStatus(execution);
  const isAlive = isParallelAgentAlive(status);
  const viewExecutionLabel = t('viewExecution');
  const backLabel = t('back');
  const canViewExecution = isNeo && !!onOpenObject && !!execution.agentThreadId;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const [chatSurface, setChatSurface] = useState<HTMLElement | null>(null);
  const task = readSubagentTask({
    messages,
    toolCallId: execution.toolCallId,
  });
  const { items } = collectSubagentActivity({
    messages,
    parentToolCallId: execution.toolCallId,
  });
  const hasOutput = !isAlive && !!task.output;
  const activityScrollKey = items
    .map((item) =>
      item.kind === 'tool'
        ? `${item.id}:${item.status}`
        : `${item.id}:${item.text.length}:${item.isStreaming ? 's' : 'd'}`,
    )
    .join('|');

  useEffect(() => {
    setChatSurface(
      anchorRef.current?.closest<HTMLElement>('[data-copilot-chat-surface]') ??
        null,
    );
  }, []);

  useEffect(() => {
    backButtonRef.current?.focus();
  }, [chatSurface]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onBack();
      }
    };

    document.addEventListener('keydown', closeOnEscape);

    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onBack]);

  useEffect(() => {
    const node = scrollerRef.current;

    if (!node) {
      return;
    }

    node.scrollTop = node.scrollHeight;
  }, [activityScrollKey, hasOutput, execution.toolCallId]);

  const panel = (
    <Box
      position="absolute"
      inset="0"
      zIndex="5"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={3}
      py={4}
      data-testid="subagent-activity-overlay"
    >
      <Box
        position="absolute"
        inset="0"
        style={{ background: 'rgba(0, 0, 0, 0.35)' }}
        onClick={onBack}
        aria-hidden
      />
      <Box
        role="dialog"
        aria-modal
        aria-label={task.title ? `${name} · ${task.title}` : name}
        position="relative"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        width="full"
        maxW="full"
        bg="bg.default"
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="lg"
        boxShadow="lg"
        animation="fade-in 0.15s ease-out"
        data-testid="subagent-activity-panel"
        style={{ height: 'min(70%, 70dvh)', minHeight: '220px' }}
      >
        <HStack
          gap={2}
          alignItems="center"
          px={2}
          py={2}
          borderBottomWidth="1px"
          borderColor="border.subtle"
          flexShrink={0}
        >
          <IconButton
            ref={backButtonRef}
            aria-label={backLabel}
            size="xs"
            variant="ghost"
            onClick={onBack}
          >
            <ArrowLeft01Icon size={16} />
          </IconButton>
          <AgentStatusDots status={status} label={tStatus(status)} />
          <VStack alignItems="stretch" gap={0} flex="1" minW={0}>
            <HStack gap={1.5} minW={0} alignItems="baseline">
              <BodyStrong truncate flexShrink={0} maxW="50%">
                {name}
              </BodyStrong>
              {task.title ? (
                <MetaDefault
                  color="fg.muted"
                  truncate
                  minW={0}
                  data-testid="subagent-task-title"
                >
                  {task.title}
                </MetaDefault>
              ) : null}
            </HStack>
            <MetaDefault color="fg.muted" truncate>
              {tStatus(status)}
            </MetaDefault>
          </VStack>
          {canViewExecution ? (
            <Button
              variant="ghost"
              size="xs"
              flexShrink={0}
              onClick={() =>
                onOpenObject?.({
                  type: 'execution',
                  id: execution.agentThreadId,
                  label: viewExecutionLabel,
                })
              }
            >
              {viewExecutionLabel}
            </Button>
          ) : null}
        </HStack>
        {task.prompt ? (
          <Box
            px={3}
            pt={3}
            pb={0}
            flexShrink={1}
            minH={0}
            maxH="22%"
            display="flex"
            flexDirection="column"
          >
            <SubagentParentMessage message={task.prompt} />
          </Box>
        ) : null}
        <Box
          ref={scrollerRef}
          flex="1"
          minH={0}
          display="flex"
          flexDirection="column"
          overflow={showsNeoSteps ? 'hidden' : 'auto'}
          px={3}
          py={3}
        >
          {showsNeoSteps ? (
            neoSteps
          ) : (
            <VStack alignItems="stretch" gap={4} width="full">
              <PanelSection
                label={t('agentActivity')}
                testId="subagent-activity"
              >
                {items.length > 0 ? (
                  <VStack alignItems="stretch" gap={2} width="full">
                    {items.map((item) => (
                      <SubagentActivityLine
                        key={item.id}
                        item={item}
                        expansionKey={`${execution.toolCallId}:${item.id}`}
                        onOpenActivity={onOpenActivity}
                        onOpenObject={onOpenObject}
                      />
                    ))}
                  </VStack>
                ) : (
                  <MetaDefault
                    color="fg.muted"
                    animation={
                      isAlive
                        ? 'shimmer-opacity 2s ease-in-out infinite'
                        : undefined
                    }
                    _motionReduce={{ animation: 'none' }}
                  >
                    {isAlive
                      ? (config.runningText ?? t('agentStarting'))
                      : tStatus(status)}
                  </MetaDefault>
                )}
              </PanelSection>
              {hasOutput && task.output ? (
                <PanelSection label={t('agentOutput')} testId="subagent-output">
                  <ToolResultRenderer
                    data={prepareToolResultData({
                      content: task.output,
                      toolName: execution.agentType,
                    })}
                    onOpenObject={onOpenObject}
                  />
                </PanelSection>
              ) : null}
              {status === 'error' && task.error ? (
                <BodyDefault color="fg.error">{task.error}</BodyDefault>
              ) : null}
            </VStack>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <span ref={anchorRef} hidden />
      {chatSurface ? createPortal(panel, chatSurface) : null}
    </>
  );
}

function PanelSection({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <VStack alignItems="stretch" gap={1.5} width="full" data-testid={testId}>
      <LabelDefault color="fg.muted">{label}</LabelDefault>
      {children}
    </VStack>
  );
}
